import React, { FC, useCallback, useState, useEffect, useMemo } from 'react';
import { useTranslate } from '@/translations';
import { FORMAT_FULL_TIME_DAYJS, StorageKeys } from '@/utils/constants';
import { AppPaths } from '@/routes';
import { useNavigate } from 'react-router-dom';
import {
  LicensePlate,
  faAngleRight,
  Icon,
  CustomColumns,
  CustomTable,
  VehicleInformation,
} from '@/ui/components';

import {
  useInspectionsVehiclesFilters,
  useInspectionsVehiclesInsights,
  useInspectionsVehiclesList,
} from '@/api/Inspections';
import { runInspectionsExport } from '@/api/Exports';
import {
  AppSearch,
  AppUserTypes,
  Damage,
  Inspection,
  InspectionInsightAgeBand,
  InspectionInsightCardKey,
  InspectionInsightDrill,
  InspectionInsightScope,
} from '@/types';
import type { IDownloadTypes } from '@/ui/components/CustomTable/exportToFile';
import {
  currencyFormatted,
  sectorConverter,
  timeConverter,
} from '@/utils/helpers';
import { defaultPageSize } from './constants';
import { InspectionIdContainer } from './InspetionsDataGrid.styled';
import { InspectionInsights } from './InspectionInsights/InspectionInsights';
import {
  loadInsightPreferences,
  saveInsightPreferences,
} from './InspectionInsights/insightPreferences';
import { Card } from 'antd';
import { useAppCustomers } from '@/hooks';

export const InspectionsDataGrid: FC = () => {
  const { t } = useTranslate();
  const { userPermissions } = useAppCustomers();
  const showInspectionInsights =
    userPermissions.userType === AppUserTypes.InternalUsers;

  const { data: filterOptions, isFetching: filtersLoading } =
    useInspectionsVehiclesFilters();

  const navigate = useNavigate();

  const [query, setQuery] = useState<AppSearch<Inspection>>();
  const [insightPrefs, setInsightPrefs] = useState(loadInsightPreferences);
  const [insightDrill, setInsightDrill] = useState<InspectionInsightDrill>({});
  const defaultAppSearch: AppSearch<Inspection> = useMemo(
    () => ({
      pageSize: defaultPageSize,
      filters: [],
      sortField: 'inspectionDateTime',
      sortOrder: 'descend',
    }),
    [],
  );

  const { data: searchResponse, isFetching: vehiclesLoading } =
    useInspectionsVehiclesList(query, {
      insightCard: insightDrill.card,
      insightAgeBand: insightDrill.ageBand,
    });
  const { data: insights, isFetching: insightsLoading } =
    useInspectionsVehiclesInsights(query, {
      scope: insightPrefs.scope,
      chartPeriodDays: insightPrefs.chartPeriodDays,
      enabled: showInspectionInsights,
    });

  const data = searchResponse?.entities ?? [];
  const total = searchResponse?.totalCount ?? 0;

  useEffect(() => {
    if (searchResponse === undefined || query === undefined) return;
    const entities = searchResponse.entities ?? [];
    if ((query.page || 1) > 1 && entities.length === 0) {
      setQuery((prev) =>
        prev ? { ...prev, page: 1, pageSize: defaultPageSize } : prev,
      );
    }
  }, [searchResponse, query]);

  const getExportData = async (type: IDownloadTypes = 'xlsx') => {
    if (!query) return [];
    await runInspectionsExport(query, type, false, total);
    return [];
  };

  const getInspectionAndDamageExportData = async (
    type: IDownloadTypes = 'xlsx',
  ) => {
    if (!query) return { extraData: [], defaultData: [] };
    await runInspectionsExport(query, type, true, total);
    return { extraData: [], defaultData: undefined };
  };

  const damageExportColumns: CustomColumns<Damage> = useMemo(
    () => [
      {
        dataIndex: 'inspectionId',
        title: t('inspectionId'),
        exportRender: (value) => (value != null ? String(value) : ''),
      },
      {
        dataIndex: 'id',
        title: t('damageId'),
        exportRender: (value) => (value != null ? String(value) : ''),
      },
      {
        dataIndex: 'damageAreaDesc',
        title: t('location'),
      },
      {
        dataIndex: 'damagePartDesc',
        title: t('partComp'),
      },
      {
        dataIndex: 'damageTypeDesc',
        title: t('damageType'),
      },
      {
        dataIndex: 'sector',
        title: t('sector'),
        exportRender: (value) => sectorConverter(value),
      },
      {
        dataIndex: 'repairActionDesc',
        title: t('repairMethod'),
      },
      {
        dataIndex: 'lineCost',
        title: 'Individual Line Cost',
        exportRender: (value) => currencyFormatted(value) ?? '',
      },
      {
        dataIndex: 'authorizationNotes',
        title: t('authorizationNotes'),
      },
    ],
    [t],
  );

  const columns: CustomColumns<Inspection> = useMemo(
    () => [
      {
        dataIndex: 'id',
        title: t('inspectionId'),
        renderType: 'number',
        width: 140,
        render: (value) => (
          <InspectionIdContainer
            onClick={() => {
              navigate(`/${AppPaths.inspection}/${value}`);
            }}
          >
            {value} <Icon icon={faAngleRight} />
          </InspectionIdContainer>
        ),
        exportRender: (value) => value.toString(),
        sortable: true,
        filter: true,
        fixed: 'left',
      },
      {
        dataIndex: 'regNumber',
        title: t('registration'),
        width: 150,
        align: 'center',
        sortable: true,
        filter: true,
        render: (value) =>
          value ? <LicensePlate regNumber={value} /> : t('noRegNumber'),
        exportRender: (value) => value || t('noRegNumber'),
      },
      {
        dataIndex: 'vin',
        title: t('vin'),
        width: 200,
        sortable: true,
        filter: true,
        render: (value) => <VehicleInformation vin={value} showAsLabel />,
        exportRender: (value) => value || '',
      },
      {
        dataIndex: 'customerName',
        title: t('customer'),
        renderType: 'select',
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.customers || [],
      },
      {
        dataIndex: 'fleetDescription',
        title: t('subCustomer'),
        render: (_, record) =>
          record.subCustomerId
            ? record.subCustomerName
            : record.fleetDescription,
        sortable: true,
        filter: true,
        ellipsis: true,
      },
      {
        dataIndex: 'inspectionLocation',
        title: t('inspectedAt'),
        renderType: 'select',
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.inspectionLocations || [],
      },
      {
        dataIndex: 'fleetCode',
        title: t('fleetCode'),
        renderType: 'select',
        width: 110,
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.fleetCodes || [],
      },
      {
        dataIndex: 'inspectionType',
        title: t('inspectionType'),
        renderType: 'select',
        width: 100,
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.inspectionTypes || [],
        ellipsis: true,
      },
      {
        dataIndex: 'model',
        title: t('model'),
        width: 150,
        sortable: true,
        filter: true,
        ellipsis: true,
      },
      {
        dataIndex: 'inspectionDateTime',
        title: t('inspectionDate'),
        renderType: 'date',
        width: 180,
        sortable: true,
        filter: true,
        render: (value) => timeConverter(value, FORMAT_FULL_TIME_DAYJS),
      },
      {
        dataIndex: 'inspectionStatusName',
        title: t('inspectionStatus'),
        renderType: 'select',
        width: 150,
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.inspectionStatus || [],
      },
      {
        dataIndex: 'inspector',
        title: t('inspector'),
        renderType: 'select',
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.inspectors || [],
      },
      {
        dataIndex: 'approvalDateTime',
        title: 'Authorised Date',
        renderType: 'date',
        width: 180,
        sortable: true,
        filter: true,
        render: (value) => timeConverter(value, FORMAT_FULL_TIME_DAYJS),
      },
      {
        dataIndex: 'tnrStatus',
        title: t('tnrStatus'),
        renderType: 'select',
        width: 150,
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.tnrStatuses || [],
      },
    ],
    [filterOptions, navigate, t],
  );

  const updateInsightPrefs = useCallback(
    (patch: Partial<typeof insightPrefs>) => {
      setInsightPrefs((current) => {
        const next = { ...current, ...patch };
        saveInsightPreferences(next);
        return next;
      });
    },
    [],
  );

  return (
    <>
      {showInspectionInsights ? (
        <InspectionInsights
          data={insights}
          loading={insightsLoading}
          expanded={insightPrefs.expanded}
          scope={insightPrefs.scope}
          chartPeriodDays={insightPrefs.chartPeriodDays}
          drill={insightDrill}
          emptyIntersection={
            !!insightDrill.card && !vehiclesLoading && total === 0
          }
          onToggleExpanded={() =>
            updateInsightPrefs({ expanded: !insightPrefs.expanded })
          }
          onScopeChange={(scope: InspectionInsightScope) =>
            updateInsightPrefs({ scope })
          }
          onPeriodChange={(chartPeriodDays) =>
            updateInsightPrefs({ chartPeriodDays })
          }
          onSelectCard={(card: InspectionInsightCardKey) =>
            setInsightDrill({ card })
          }
          onSelectAgeBand={(
            card: InspectionInsightCardKey,
            ageBand: InspectionInsightAgeBand,
          ) => setInsightDrill({ card, ageBand })}
          onClearDrill={() => setInsightDrill({})}
        />
      ) : null}
      <Card>
        <CustomTable
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={vehiclesLoading || filtersLoading}
          pagination={{
            total,
            pageSize: query?.pageSize,
            current: query?.page || 1,
          }}
          exportOptions={{
            fileName: 'Inspections',
            skipClientExport: true,
            getExportData,
            extraExports: [
              {
                key: 'damages',
                label: 'Inspection & Damages',
                fileName: 'Inspection & Damages',
                skipClientExport: true,
                columns: damageExportColumns,
                getExportData: getInspectionAndDamageExportData,
              },
            ],
          }}
          appSearchOptions={{
            storageKey: StorageKeys.SearchInspections,
            defaultSearch: defaultAppSearch,
            value: query,
            onChange: setQuery,
          }}
          scroll={{ x: 2100 }}
        />
      </Card>
    </>
  );
};
